import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { get, apiErrorMessage } from '../../helpers/api';
import { useCopyToClipboard, useFormatters, useToast } from '../../hooks';
import { cardBodySx, scrollY } from '../../ui/styles';
import { Box, Button, IconButton, LinearProgress, Paper, Stack, Typography } from '../../ui/core';
import { ContentCopyIcon } from '../../ui/icons';
import { StandbyStatusChip } from '../../components/widgets/Rides/StandbyStatusChip';
import { WaypointSummary } from './WaypointSummary';

/**
 * Detail view for a standby ride request (a Postgres row with a UUID id), as
 * opposed to a confirmed ride (a Mongo document). RideDetails picks between the
 * two on id shape.
 *
 * There is no event log panel: the standby lifecycle writes no TripRideEvent
 * rows, so the timeline would always be empty — and requesting
 * /admin/ride/:id/events with a UUID would 400 and toast an error.
 */
const StandbyRideDetails = ({ id }) => {
  const f = useFormatters();
  const c = useCopyToClipboard();
  const t = useToast();

  const [standby, setStandby] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setNotFound(false);
      setErrorText(null);
      setStandby(null);
      try {
        const response = await get(`/admin/standby-ride/${id}`);
        if (cancelled) return;
        setStandby(response.data);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          console.error('Error fetching standby ride request:', err);
          setErrorText(apiErrorMessage(err) || 'There was a problem fetching this standby ride request.');
          t.error('There was an error loading this standby ride request');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const copyIdToClipboard = () => {
    c.copy(id);
    t.success('Standby request id copied to clipboard');
  };

  if (loading) {
    return (
      <Stack sx={{ ...cardBodySx, position: 'relative', ...scrollY }} alignItems="center">
        <LinearProgress sx={{ width: '300px', marginTop: '300px' }} />
      </Stack>
    );
  }

  if (notFound) {
    return (
      <Stack sx={cardBodySx} alignItems="center" justifyContent="center" spacing="8px">
        <Typography variant="h5">Standby request not found</Typography>
        <Typography color="text.secondary">No standby ride request with id {id}.</Typography>
      </Stack>
    );
  }

  if (errorText) {
    return (
      <Stack sx={cardBodySx} alignItems="center" justifyContent="center" spacing="8px">
        <Typography variant="h5">Unable to load standby request</Typography>
        <Typography color="text.secondary">{errorText}</Typography>
      </Stack>
    );
  }

  if (!standby) {
    return null;
  }

  const riderName = standby.rider
    ? `${standby.rider.firstName || ''} ${standby.rider.lastName || ''}`.trim()
    : null;

  const timeRangeLabel = (range) => {
    const start = range.start ? f.date.toLocaleString(range.start, f.date.DateFormats.DATETIME_MED) : '—';
    const end = range.end ? f.date.toLocaleString(range.end, f.date.DateFormats.DATETIME_MED) : '—';
    return `${start} – ${end}`;
  };

  return (
    <Stack sx={{ ...cardBodySx, position: 'relative', ...scrollY }} spacing={2}>
      <Paper variant="outlined" sx={{ padding: '12px 32px' }}>
        <Stack spacing="12px">
          <Stack direction="row" spacing="16px" alignItems="center">
            <Typography variant="h5" noWrap>
              Standby request {standby.id}
            </Typography>
            <IconButton size="small" onClick={copyIdToClipboard}>
              <ContentCopyIcon sx={{ fontSize: '16px' }} />
            </IconButton>
            <StandbyStatusChip status={standby.standbyStatus} />
            {standby.confirmedRideId && (
              <Button component={Link} to={`/rides/${standby.confirmedRideId}`}>
                View confirmed ride
              </Button>
            )}
          </Stack>
          <Stack direction="row" spacing="48px" alignItems="center" flexWrap="wrap">
            <Stack direction="row" spacing="8px">
              <Typography color="text.secondary" variant="body2">
                Rider
              </Typography>
              {standby.rider ? (
                <Button
                  component={Link}
                  to={`/users/${standby.rider.id}`}
                  sx={{ padding: 0, minWidth: 0, textTransform: 'none' }}
                >
                  {riderName || standby.rider.id}
                </Button>
              ) : (
                <Typography>—</Typography>
              )}
            </Stack>
            <Stack direction="row" spacing="8px">
              <Typography color="text.secondary" variant="body2">
                Fare
              </Typography>
              <Typography>
                {standby.fare && standby.fare.finalAmount != null ? f.number.currency(standby.fare.finalAmount) : '—'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing="8px">
              <Typography color="text.secondary" variant="body2">
                Seats
              </Typography>
              <Typography>1</Typography>
              <Typography color="text.secondary" variant="body2">
                (standby requests are always single-seat)
              </Typography>
            </Stack>
            <Stack direction="row" spacing="8px">
              <Typography color="text.secondary" variant="body2">
                Platform
              </Typography>
              <Typography>{standby.platform || '—'}</Typography>
            </Stack>
            <Stack direction="row" spacing="8px">
              <Typography color="text.secondary" variant="body2">
                Requested at
              </Typography>
              <Typography>
                {standby.requestedAt
                  ? f.date.toLocaleString(standby.requestedAt, f.date.DateFormats.DATETIME_MED)
                  : '—'}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" spacing="8px">
            <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              Departure date
            </Typography>
            <Typography>
              {/* departureDate is a Postgres @db.Date, serialized as UTC midnight
                  (e.g. "2026-08-20T00:00:00.000Z"). UTC midnight *is* the intended
                  calendar date, so format it in UTC rather than browser-local —
                  otherwise an admin west of UTC sees the day before. */}
              {standby.departureDate
                ? f.date.toTimezoneLocaleString(standby.departureDate, 'UTC', f.date.DateFormats.DATE_MED)
                : '—'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing="48px" flexWrap="wrap">
            <WaypointSummary label="Pick-up" waypoint={standby.pickUp} />
            <Box>
              <Typography> ➔ </Typography>
            </Box>
            <WaypointSummary label="Drop-off" waypoint={standby.dropOff} />
          </Stack>
          <Stack direction="row" spacing="48px" flexWrap="wrap">
            <Stack direction="row" spacing="8px">
              <Typography color="text.secondary" variant="body2">
                Origin station
              </Typography>
              <Typography>{standby.originStationName || '—'}</Typography>
            </Stack>
            <Stack direction="row" spacing="8px">
              <Typography color="text.secondary" variant="body2">
                Destination station
              </Typography>
              <Typography>{standby.destinationStationName || '—'}</Typography>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ padding: '12px 32px 24px 32px' }}>
        <Typography variant="subtitle1" sx={{ marginTop: '8px' }}>
          Candidate times
        </Typography>
        <Stack spacing="8px" sx={{ marginTop: '8px' }}>
          <Stack direction="row" spacing="8px">
            <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              Departure times
            </Typography>
            <Typography>
              {standby.departureTimes && standby.departureTimes.length > 0
                ? standby.departureTimes
                    .map((time) => f.date.toLocaleString(time, f.date.DateFormats.DATETIME_MED))
                    .join(', ')
                : '—'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing="8px">
            <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              Arrival times
            </Typography>
            <Typography>
              {standby.arrivalTimes && standby.arrivalTimes.length > 0
                ? standby.arrivalTimes
                    .map((time) => f.date.toLocaleString(time, f.date.DateFormats.DATETIME_MED))
                    .join(', ')
                : '—'}
            </Typography>
          </Stack>
          {standby.departureTimeRanges && standby.departureTimeRanges.length > 0 && (
            <Stack direction="row" spacing="8px">
              <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                Departure time ranges
              </Typography>
              <Typography>{standby.departureTimeRanges.map(timeRangeLabel).join(', ')}</Typography>
            </Stack>
          )}
          {standby.arrivalTimeRanges && standby.arrivalTimeRanges.length > 0 && (
            <Stack direction="row" spacing="8px">
              <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                Arrival time ranges
              </Typography>
              <Typography>{standby.arrivalTimeRanges.map(timeRangeLabel).join(', ')}</Typography>
            </Stack>
          )}
          <Stack direction="row" spacing="8px">
            <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              Notify by
            </Typography>
            <Typography>
              {standby.notifyByTime
                ? f.date.toLocaleString(standby.notifyByTime, f.date.DateFormats.DATETIME_MED)
                : '—'}
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ padding: '12px 32px 24px 32px' }}>
        <Typography variant="subtitle1" sx={{ marginTop: '8px' }}>
          Payment
        </Typography>
        <Stack spacing="8px" sx={{ marginTop: '8px' }}>
          <Stack direction="row" spacing="8px">
            <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              Status
            </Typography>
            <Typography>{standby.payment?.status || '—'}</Typography>
          </Stack>
          <Stack direction="row" spacing="8px">
            <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              Auth payment intent id
            </Typography>
            <Typography>{standby.payment?.authPaymentIntentId || '—'}</Typography>
          </Stack>
          <Stack direction="row" spacing="8px">
            <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              Payment method id
            </Typography>
            <Typography>{standby.payment?.paymentMethodId || '—'}</Typography>
          </Stack>
          <Stack direction="row" spacing="8px">
            <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              Charge id
            </Typography>
            <Typography>{standby.payment?.chargeId || '—'}</Typography>
          </Stack>
          {standby.payment?.cancellationRefundInfo != null && (
            <Stack direction="row" spacing="8px">
              <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                Cancellation refund info
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(standby.payment.cancellationRefundInfo)}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ padding: '12px 32px 24px 32px' }}>
        <Typography variant="subtitle1" sx={{ marginTop: '8px' }}>
          Identifiers
        </Typography>
        <Stack spacing="8px" sx={{ marginTop: '8px' }}>
          <Stack direction="row" spacing="8px">
            <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              Route id
            </Typography>
            <Typography>{standby.routeId || '—'}</Typography>
          </Stack>
          <Stack direction="row" spacing="8px">
            <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              Product id
            </Typography>
            <Typography>{standby.productId || '—'}</Typography>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default StandbyRideDetails;
